import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { BILLING } from '@lib/constants/billing'
import { formatCents, microsToWholeCents, parseDollarsToCents } from '@lib/helpers/money'
import { setLowBalanceThresholdInputSchema } from '@lib/input/billing.input'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, fieldMessageId } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useBillingActions } from '@/hooks/useBilling'

/** Why an amount typed in dollars can't be the threshold. */
function amountMessage(cents: number | null): string {
  if (cents === null) return 'Enter an amount like 5 or 5.00.'
  return `Enter at most ${formatCents(BILLING.thresholdMaxCents)}.`
}

interface ThresholdFormProps {
  currentCents: number
  onDone: () => void
}

/** Mounted each time the dialog opens, so it starts at the current threshold. */
function ThresholdForm({ currentCents, onDone }: ThresholdFormProps) {
  const { setLowBalanceThreshold } = useBillingActions()
  const [amount, setAmount] = React.useState(() => (currentCents / 100).toFixed(2))
  const [error, setError] = React.useState<string | null>(null)
  const [formError, setFormError] = React.useState<string | null>(null)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const cents = parseDollarsToCents(amount)
    const parsed = setLowBalanceThresholdInputSchema.safeParse({ thresholdCents: cents ?? Number.NaN })
    if (!parsed.success) {
      setError(amountMessage(cents))
      return
    }
    setError(null)
    setFormError(null)
    try {
      await setLowBalanceThreshold.mutateAsync(parsed.data)
      toast.success(
        parsed.data.thresholdCents === 0
          ? 'Low-balance alert turned off'
          : `You'll be alerted when the balance falls below ${formatCents(parsed.data.thresholdCents)}`,
      )
      onDone()
    } catch (failure) {
      setFormError(apiErrorMessage(failure, 'Could not change the alert.'))
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <DialogHeader>
        <DialogTitle>Low-balance alert</DialogTitle>
        <DialogDescription>
          When a live send takes the balance below this amount, Owners get an email and your live webhook gets{' '}
          <span className="font-mono">billing.balance_low</span>. It fires once each time the balance drops below it.
        </DialogDescription>
      </DialogHeader>
      <Field
        label="Alert when the balance falls below"
        htmlFor="threshold-amount"
        error={error}
        hint={`Up to ${formatCents(BILLING.thresholdMaxCents)}. $0.00 turns the alert off.`}
      >
        <Input
          id="threshold-amount"
          inputMode="decimal"
          value={amount}
          aria-invalid={error ? true : undefined}
          aria-describedby={fieldMessageId('threshold-amount')}
          onChange={(event) => setAmount(event.target.value)}
        />
      </Field>
      {formError ? <p className="text-body text-danger">{formError}</p> : null}
      <DialogFooter>
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={setLowBalanceThreshold.isPending}>
          {setLowBalanceThreshold.isPending ? <Loader2 className="animate-spin" /> : null}
          Save
        </Button>
      </DialogFooter>
    </form>
  )
}

interface ThresholdDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  thresholdMicros: number
}

/** An Owner sets the balance below which the low-balance alert fires. */
export function ThresholdDialog({ open, onOpenChange, thresholdMicros }: ThresholdDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <ThresholdForm currentCents={microsToWholeCents(thresholdMicros)} onDone={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  )
}
